import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ThumbsUp, ThumbsDown, Volume2, Square } from 'lucide-react';
import SourceAccordion from './SourceAccordion';
import StreamingIndicator from './StreamingIndicator';
import { useSpeechSynthesis } from '../../hooks/useSpeechSynthesis';

interface MessageBubbleProps {
  role: 'user' | 'assistant';
  content: string;
  searchResults?: any[];
  isStreaming?: boolean;
  streamingPhase?: 'rewriting' | 'searching' | 'generating' | 'streaming';
  onSourceClick?: (documentId: number, documentName: string, pageNumber?: number) => void;
  messageId?: number;
  feedbackScore?: number | null;
  onFeedback?: (messageId: number, score: number) => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  role,
  content,
  searchResults,
  isStreaming,
  streamingPhase,
  onSourceClick,
  messageId,
  feedbackScore,
  onFeedback,
}) => {
  const isUser = role === 'user';

  const { supported: ttsSupported, speak, stop, speaking } = useSpeechSynthesis();
  const [thisSpeaking, setThisSpeaking] = React.useState(false);

  // When global speaking flips to false, this bubble is no longer speaking
  // (utterance finished, or another bubble/auto-read called cancel()). Without
  // this the icon sticks on "stop" until the next click (Codex P2-c).
  React.useEffect(() => {
    if (!speaking) setThisSpeaking(false);
  }, [speaking]);

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      <div
        className={`max-w-[85%] lg:max-w-xl min-w-0 break-words rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-blue-600 text-white'
            : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'
        }`}
      >
        {/* Streaming indicator (searching / generating phases) */}
        {isStreaming && streamingPhase && streamingPhase !== 'streaming' && !content && (
          <StreamingIndicator phase={streamingPhase} />
        )}

        {/* Message content */}
        {content && (
          isUser ? (
            <p className="whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{content}</p>
          ) : (
            <div className="prose prose-sm dark:prose-invert max-w-none break-words [overflow-wrap:anywhere] prose-code:break-all prose-pre:overflow-x-auto prose-pre:max-w-full">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
            </div>
          )
        )}

        {/* Blinking cursor during token streaming */}
        {isStreaming && streamingPhase === 'streaming' && (
          <span className="inline-block w-2 h-4 bg-gray-400 dark:bg-gray-500 animate-pulse ml-0.5 align-text-bottom" />
        )}

        {/* Source accordion (below streaming text) */}
        {!isUser && searchResults && searchResults.length > 0 && (
          <SourceAccordion sources={searchResults} onSourceClick={onSourceClick} />
        )}

        {/* Feedback buttons (assistant messages only) */}
        {!isUser && messageId && !isStreaming && (
          <div className="flex items-center gap-2 mt-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            {ttsSupported && content && (
              <button
                onClick={() => {
                  if (thisSpeaking && speaking) {
                    stop();
                    setThisSpeaking(false);
                  } else {
                    setThisSpeaking(true);
                    speak(content, { onBlocked: () => setThisSpeaking(false) });
                  }
                }}
                className={`p-1 rounded transition-colors ${
                  thisSpeaking && speaking
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'
                }`}
                title={thisSpeaking ? 'Stop' : 'Read aloud'}
                aria-label={thisSpeaking ? 'Stop reading' : 'Read answer aloud'}
              >
                {thisSpeaking && speaking ? (
                  <Square className="w-3.5 h-3.5" fill="currentColor" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5" />
                )}
              </button>
            )}
            <button
              onClick={() => onFeedback?.(messageId, feedbackScore === 1 ? 0 : 1)}
              className={`p-1 rounded transition-colors ${
                feedbackScore === 1
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400'
              }`}
              title="Helpful"
            >
              <ThumbsUp className="w-3.5 h-3.5" fill={feedbackScore === 1 ? 'currentColor' : 'none'} />
            </button>
            <button
              onClick={() => onFeedback?.(messageId, feedbackScore === -1 ? 0 : -1)}
              className={`p-1 rounded transition-colors ${
                feedbackScore === -1
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400'
              }`}
              title="Not helpful"
            >
              <ThumbsDown className="w-3.5 h-3.5" fill={feedbackScore === -1 ? 'currentColor' : 'none'} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;
